import type { Palabra } from '@igor/core';
import { idiomaActual, traducir } from './idioma';
import { supabase } from './supabase';

/** Saldo de créditos de la cuenta. Vive en la base, no en la API: la pantalla lo lee con sus propias reglas. */
export async function creditosDisponibles(): Promise<{ disponibles: number; sinLimite: boolean } | null> {
  const { data, error } = await supabase.from('credit_balance').select('available, unlimited').maybeSingle();
  if (error || !data) return null;
  const fila = data as { available: number | null; unlimited: boolean | null };
  return { disponibles: fila.available ?? 0, sinLimite: !!fila.unlimited };
}

/**
 * Todo lo que escribe o cuesta dinero pasa por la API del Worker, nunca por el navegador. Cada petición dice en qué
 * idioma está la interfaz (`X-Idioma`) y los errores vienen ya en ese idioma, listos para mostrar tal cual.
 */
const BASE = import.meta.env.VITE_API ?? 'https://igor-api.ideasenmovimiento.com.co';

export class ErrorDeApi extends Error {
  constructor(mensaje: string, readonly estado: number, readonly datos?: Record<string, unknown>) {
    super(mensaje);
  }
}

/**
 * La sesión dura una hora y el navegador retrasa su renovación cuando la pestaña queda en segundo plano: por eso
 * se renueva aquí si vence en menos de un minuto y, si el motor igual la rechaza (401), se renueva y se reintenta
 * una vez antes de mostrar el error.
 */
async function tokenVigente(forzar = false): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  const s = data.session;
  if (s && (forzar || (s.expires_at ?? 0) * 1000 - Date.now() < 60_000)) {
    const { data: nueva } = await supabase.auth.refreshSession();
    return nueva.session?.access_token ?? (forzar ? undefined : s.access_token);
  }
  return s?.access_token;
}

async function pide<T>(ruta: string, metodo = 'GET', cuerpo?: unknown): Promise<T> {
  const llamar = (token?: string) =>
    fetch(BASE + ruta, {
      method: metodo,
      headers: {
        'Content-Type': 'application/json',
        'X-Idioma': idiomaActual(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
    });
  let res = await llamar(await tokenVigente());
  if (res.status === 401) {
    const nuevo = await tokenVigente(true).catch(() => undefined);
    if (nuevo) res = await llamar(nuevo);
  }
  const texto = await res.text();
  const json = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
  if (!res.ok) throw new ErrorDeApi(String(json.error ?? traducir('No pudimos conectarnos. Inténtalo otra vez.')), res.status, json);
  return json as T;
}

// ---------------------------------------------------------------- tipos que devuelve el motor

export type Paso =
  | 'descubrimiento' | 'estrategia' | 'auditoria' | 'parrilla' | 'perfil' | 'estilo'
  | 'textos' | 'disenos' | 'presentador' | 'videos' | 'elenco' | 'estudio';

export type EstadoDePaso = { id: string; version: number; status: 'draft' | 'approved'; data: Record<string, unknown>; created_at: string } | null;

export type Trabajo = {
  id: string;
  type: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  progress: number;
  message: string;
  /** Cuándo empezó (para decir cuánto falta aunque se haya salido y vuelto a la página). */
  created_at?: string;
};

/** Un personaje, lugar u objeto de «Tu elenco», con sus imágenes firmadas. */
export type ElementoDeElenco = import('@igor/core').Elemento & { imagenUrl: string | null; hojaUrl: string | null };
/** Lo que se cambia gratis de un plano: las columnas de la lista de planos y cómo se anima. */
export type CambiosDePlano = {
  movimiento?: string; segundos?: number; dialogo?: string; hastaSiguiente?: boolean; usarVersion?: string;
  encuadre?: string; angulo?: string; lenteMm?: number; equipo?: string; notas?: string;
  nuevoFotograma?: string; falla?: 'parecido' | 'accion' | 'encuadre' | 'raro';
};
type PlanoFirmado = import('@igor/core').PlanoDeProduccion & { fotogramaUrl: string | null; clipUrl: string | null; versionesUrls?: Array<{ ruta: string; url: string | null }> };
export type ProduccionDetalle = {
  resultadoId: string;
  estado: string;
  produccion: Omit<import('@igor/core').ProduccionEstudio, 'planos'> & {
    planos: PlanoFirmado[];
    videoUrl: string | null;
    cancionUrl: string | null;
    /** Las pistas del montaje, firmadas. null = todavía no se montó (o se montó con la versión vieja). */
    audioUrls: { voces: Array<{ n: number; ms: number; url: string | null }>; efectos: Record<string, string | null>; musica: string | null; duracionesMs: number[] } | null;
  };
  /** Las capturas reales de la app y la web de la marca (para los planos de pantalla). */
  capturasUrls?: { movil: string[]; web: string[] };
  /** Voces para el narrador: la elegida primero. Muestra en /voces/<id>.mp3. */
  voces: Array<{ id: string; nombre: string; region: string; genero: 'f' | 'm' }>;
  elenco: ElementoDeElenco[];
  trabajo: Trabajo | null;
  /** País de la marca (para las voces de los personajes). */
  pais?: string | null;
  /** Pantallas de la app/web que muestra el video sin capturas reales todavía. */
  capturasFaltan?: Array<'movil' | 'web' | 'logo'>;
};

export type Marca = { id: string; name: string; niche?: string; status?: string; /** Solo en la lista de marcas. */ logoUrl?: string | null; country?: string | null; /** Zona horaria elegida (vacía = la del país). */ timezone?: string | null };

type CifraHL = { total: number; totalChange: string | number; platforms: Record<string, { value: number; change: string | number }> };
export type Medicion = {
  month: string;
  aviso: string | null;
  cuenta: null | {
    dayRange: string[];
    grouping: string;
    totals: { posts: number; likes: number; followers: number; impressions: number; comments: number };
    breakdowns: { posts?: CifraHL; impressions?: CifraHL; reach?: CifraHL; engagement?: Record<string, { likes: number; comments: number; shares: number; change: string | number }> };
    platformTotals: Record<string, Record<string, { total: number; series: number[] }>>;
  };
  piezas: Array<{ piece_index: number; platform: string; url: string | null; published_at: string | null; likes: number; comments: number; shares: number; views: number | null; followers: number | null; measured_at: string }>;
  publicaciones: Array<{ piece_index: number; platform: string; status: string; scheduled_at: string }>;
  ultima: null | { status: string; started_at: string; finished_at: string | null; matched: number; error: string | null };
};

export type MarcaResumen = {
  id: string; name: string; niche: string | null; status: string; created_at: string; logoUrl: string | null;
  marcaLista: number; marcaTotal: number; mesListo: number; mesTotal: number; mesEmpezado: boolean;
  programadas: number; publicadas: number; interacciones: number; vistas: number;
};
export type Cuenta0 = {
  usuario: { email?: string };
  workspace: { nombre: string; estado: string; desde: string } | null;
  plan: { id: string; name: string; monthly_credits: number; price_usd_month: number; brand_limit: number; human_review: boolean } | null;
  suscripcion: { plan_id: string; billing_interval: string; status: string; current_period_end: string | null; cancel_at: string | null } | null;
  creditos?: { disponibles: number; sinLimite: boolean; usadosEsteMes: number; recibidosEsteMes: number; vencenPronto: { cantidad: number; fecha: string } | null };
  movimientos?: Array<{ delta: number; kind: string; reason: string | null; created_at: string }>;
  limite?: number | null;
  mes?: string;
  /** El programa de referidos: su código y cuántos amigos llegaron y compraron. */
  referidos?: {
    codigo: string; recompensa: number; invitados: number; compraron: number;
    invitadoPor: 'pendiente' | 'premiado' | null; puedeUsarCodigo: boolean;
  } | null;
  marcas: MarcaResumen[];
};

export type Cuenta = {
  id: string;
  nombre: string;
  red: string;
  redNombre: string;
  tipo: string;
  foto?: string;
  etiqueta?: string;
  /** La autorización de la red vence (Instagram ~60 días): hay que volver a conectarla. */
  vence?: string | null;
  vencida?: boolean;
  porVencer?: boolean;
};

/** Lo que cuesta cada paso, en créditos. Los manda el motor para no escribir números a mano en la pantalla. */
export type Precios = {
  descubrimiento: number; estrategia: number; auditoriaBase: number; auditoriaRed: number; parrilla: number;
  textoPieza: number; estilo: number; estiloCrear: number; perfil: number; presentador: number; reelVideo: number;
  estatica: number; carrusel: number; portadaReel: number;
};

export const api = {
  yo: () => pide<{ user: { email: string }; precios: Precios }>('/v1/me'),
  /** `ref` es el código de invitación con el que llegó (si llegó con uno). */
  arrancar: (ref?: string | null, perfil?: Record<string, unknown> | null) => pide<{ prueba?: 'dada' | 'repetida' | 'desechable' | 'limite' | null }>('/v1/bootstrap', 'POST', { ...(ref ? { ref } : {}), ...(perfil ? { perfil } : {}) }),
  perfil: () => pide<{ email: string; nombre: string; telefono: string; pais: string; negocio: string; marketing: boolean; completo: boolean }>('/v1/perfil'),
  guardarPerfil: (p: { nombre?: string; telefono?: string; pais?: string; negocio?: string; marketing?: boolean }) => pide<{ ok: true }>('/v1/perfil', 'PATCH', p),
  planes: () => pide<{
    planes: Array<{ id: string; nombre: string; creditos: number; precioMes: number; precioAnio: number | null; marcas: number; usuarios: number; equipoHumano: boolean; beneficios: Record<string, unknown>; pagarMes: string | null; pagarAnio: string | null }>;
    recarga: { minimoUsd: number; tramos: ReadonlyArray<{ desdeUsd: number; usdPorCredito: number }>; pagar: string | null };
    /** Stripe directo: qué planes se pueden pagar, los paquetes de recarga y si ya tiene facturas. null = HighLevel. */
    stripe: { planes: Array<{ plan: string; intervalo: 'month' | 'year' }>; paquetes: Array<{ id: string; creditos: number; usd: number }>; facturas: boolean } | null;
    marcaExtraCreditos: number;
    calendarioEquipo: string | null;
  }>('/v1/planes'),

  /** Abre Stripe Checkout: devuelve la dirección a la que hay que ir. */
  pagar: (pedido: { tipo: 'plan'; plan: string; intervalo: 'month' | 'year' } | { tipo: 'recarga'; paquete: string }) => pide<{ url: string }>('/v1/pagos/checkout', 'POST', pedido),
  /** Portal de Stripe: tarjeta, facturas y cancelar. */
  facturas: () => pide<{ url: string }>('/v1/pagos/portal', 'POST', {}),
  /** Portal de Stripe abierto directo en la confirmación del cambio a ese plan; al confirmar vuelve a Tu perfil. */
  cambiarPlan: (plan: string, intervalo: 'month' | 'year') => pide<{ url: string }>('/v1/pagos/portal', 'POST', { plan, intervalo }),
  /** Deshace la cancelación (el plan sigue después del periodo pagado). */
  /** Borra la cuenta: hay que mandar el correo exacto como confirmación. */
  borrarCuenta: (correo: string) => pide<{ ok: true }>('/v1/cuenta', 'DELETE', { correo }),
  reactivar: () => pide<{ ok: true }>('/v1/pagos/reactivar', 'POST', {}),
  estadoDelPago: (sesion: string) => pide<{ estado: 'esperando' | 'pendiente' | 'listo'; tipo?: 'plan' | 'recarga'; plan?: string | null }>(`/v1/pagos/estado?sesion=${encodeURIComponent(sesion)}`),

  marcas: () => pide<{ brands: Marca[] }>('/v1/brands'),
  /** El tablero de la cuenta: plan, créditos, movimientos y el avance de cada marca. */
  cuenta: () => pide<Cuenta0>('/v1/cuenta'),
  usarCodigo: (codigo: string) => pide<{ ok: true }>('/v1/referidos', 'POST', { codigo }),
  crearMarca: (name: string, extra = false) => pide<{ brand: Marca; extra?: boolean }>('/v1/brands', 'POST', { name, ...(extra ? { extra: true } : {}) }),
  /** Hay que mandar el nombre exacto: es la confirmación de que de verdad se quiere borrar. */
  borrarMarca: (id: string, nombre: string) => pide<{ ok: true }>(`/v1/brands/${id}`, 'DELETE', { nombre }),

  /** `month` elige el mes de los pasos mensuales; sin él, el motor escoge el mes en curso o el próximo planeado. */
  estado: (id: string, month?: string | null) =>
    pide<{ brand: Marca; pasos: Record<Paso, EstadoDePaso>; trabajos: Trabajo[]; creditos?: number; month: string | null; meses: string[] }>(
      `/v1/brands/${id}/estado${month ? `?month=${month}` : ''}`,
    ),
  lanzarPaso: (id: string, paso: Paso, cuerpo: { input?: unknown; feedback?: string; aplicarRespuestas?: boolean }) =>
    pide<{ jobId: string }>(`/v1/brands/${id}/pasos/${paso}`, 'POST', cuerpo),
  trabajo: (jobId: string) => pide<{ job: Trabajo }>(`/v1/jobs/${jobId}`),
  /** Cuánto tarda cada paso (minutos típicos y largos), medido con los trabajos reales. */
  tiempos: () => pide<{ tiempos: Record<string, { tipico: number; largo: number }> }>('/v1/tiempos'),
  /** Aprueba una sola pieza de los textos o los diseños del mes; con todas aprobadas, el paso queda aprobado. */
  /** Borra todas las piezas de Diseño gráfico de un mes (para diseñar de nuevo). */
  borrarDisenos: (id: string, month: string) => pide<{ ok: true }>(`/v1/brands/${id}/disenos/${month}`, 'DELETE'),
  borrarDiseno: (id: string, month: string, index: number) => pide<{ ok: true }>(`/v1/brands/${id}/disenos/${month}/${index}`, 'DELETE'),
  aprobarPieza: (id: string, cuerpo: { paso: 'textos' | 'disenos'; month: string; index: number }) =>
    pide<{ ok: true; aprobadas: number[]; completo: boolean }>(`/v1/brands/${id}/aprobar-pieza`, 'POST', cuerpo),
  aprobar: (resultadoId: string) => pide<{ ok: true }>(`/v1/results/${resultadoId}/aprobar`, 'POST', {}),
  responder: (id: string, answers: Array<{ id: string; question: string; answer: string | string[]; step?: string }>) =>
    pide<{ ok: true }>(`/v1/brands/${id}/respuestas`, 'POST', { answers }),

  /** Las opciones de modelo de un reel, con lo que cuesta cada una. No gasta créditos. */
  opcionesDeVideo: (id: string, month: string, index: number, presentador?: number, estilo?: string) =>
    pide<{
      /** Comercial animado: su estilo (null = reel normal). */
      comercial: string | null;
      formato: { id: string; nombre: string };
      sinFicha: boolean;
      presentador: { n: number; nombre: string };
      personas: Array<{ n: number; nombre: string; fotoUrl: string | null; conFicha: boolean }>;
      tomas: Array<{
        codigo: string; papel: string; habla: boolean; segundos: number; dice: string; narra: string | null; rotulo: string | null;
        seVe: string; necesitaImagen: boolean; imagenUrl: string | null;
      }>;
      segundos: number;
      faltanImagenes: number;
      creditosImagenes: number;
      opciones: Array<{ ranura: string; nombre: string; etiqueta: string; queDa: string; aCambio: string; creditos: number; recomendada: boolean }>;
      /** "Elegir voz". null = en este video nadie habla a cámara. */
      voces: { creditos: number; lista: Array<{ id: string; nombre: string; region: string; muestra: string }> } | null;
    }>(`/v1/brands/${id}/videos/opciones?month=${encodeURIComponent(month)}&index=${index}${presentador ? `&presentador=${presentador}` : ''}${estilo ? `&estilo=${estilo}` : ''}`),

  /** Estudio de animación: tu elenco y tus producciones. No gasta créditos. */
  estudio: (id: string) =>
    pide<{ elenco: ElementoDeElenco[]; pais?: string | null; producciones: Array<{ id: string; titulo: string; formato: string; estilo: string; fase: string; planos: number; portadaUrl: string | null; actualizado: string; serie: { id: string; nombre: string; episodio: number } | null; pieza: { month: string; index: number } | null; guardado?: boolean }> }>(`/v1/brands/${id}/estudio`),
  produccion: (id: string, prod: string) => pide<ProduccionDetalle>(`/v1/brands/${id}/estudio/${prod}`),
  /** Cambia el texto de la voz en off de un plano (gratis; el montaje queda por rehacer). */
  vozDePlano: (id: string, prod: string, n: number, voz: string) => pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}/plano`, 'PATCH', { n, voz }),
  /** Cómo se anima un plano (cámara, duración, frase, terminar en el siguiente) o volver a una imagen anterior. Gratis. */
  ajustarPlano: (id: string, prod: string, n: number, cambios: CambiosDePlano) =>
    pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}/plano`, 'PATCH', { n, ...cambios }),
  /** Reescribir una escena de la historia antes del storyboard (gratis). */
  escenaDeHistoria: (id: string, prod: string, n: number, texto: string) => pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}/escena`, 'PATCH', { n, texto }),
  /** Recorte, velocidad y zoom de un plano en el montaje (gratis). null = quitar los ajustes. */
  pulirPlano: (id: string, prod: string, n: number, pulido: import('@igor/core').Pulido | null) => pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}/plano`, 'PATCH', { n, pulido }),
  /** Guarda el video final (ya subido) como el video de su pieza del mes; sin pieza, lo suma al mes en el día elegido. */
  finalDeProduccion: (id: string, prod: string, cuerpo: { ruta: string; fecha?: string; hora?: string; redes?: string[] }) =>
    pide<{ ok: true; pieza: { month: string; index: number } }>(`/v1/brands/${id}/estudio/${prod}/final`, 'POST', cuerpo),
  borrarProduccion: (id: string, prod: string) => pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}`, 'DELETE'),
  elenco: (id: string, cambios: { id: string; nombre?: string; notas?: string; trasfondo?: string; borrar?: boolean; voz?: string }) =>
    pide<{ ok: true; elenco: ElementoDeElenco[] }>(`/v1/brands/${id}/elenco`, 'PATCH', cambios),

  meses: (id: string) => pide<{ meses: Array<{ period: string; estado: string; borraEl: string }> }>(`/v1/brands/${id}/meses`),

  /** Tu identidad → 3 fotos de ejemplo del estilo de la marca (usa créditos solo si salen las tres). */
  ejemplosDeFotos: (id: string) => pide<{ ok: true; ejemplos: number; cobrado: number }>(`/v1/brands/${id}/identidad/ejemplos`, 'POST', {}),
  /** Para el equipo: proveedores de IA sin saldo (vacío para los suscriptores). */
  estadoProveedores: () => pide<{ proveedores: Array<{ proveedor: string; sin_saldo: boolean; saldo_usd: number | null; detalle: string | null; actualizado: string }> }>('/v1/estado-proveedores'),
  /** Tu material: fotos y videos propios de la marca. */
  material: (id: string) => pide<{ material: Array<{ path: string; nombre: string; tipo: 'foto' | 'video'; bytes: number; creado: string; url: string | null }> }>(`/v1/brands/${id}/material`),
  borrarMaterial: (id: string, path: string) => pide<{ ok: true }>(`/v1/brands/${id}/material`, 'DELETE', { path }),
  /** Lo que se dice en un video propio, para sus subtítulos (1 crédito). */
  /** Lo que se dice en un video propio, con el momento de cada palabra (la segunda vez sale gratis). */
  transcribir: (id: string, path: string) => pide<{ texto: string; palabras?: Palabra[]; aviso?: string }>(`/v1/brands/${id}/material/transcribir`, 'POST', { path }),
  /** «Lo grabas tú»: el video editado (ya en Tu material) pasa a ser el de su publicación del mes. */
  usarGrabacion: (id: string, cuerpo: { month: string; index: number; ruta: string; formato?: 'propio' | 'animado'; conservar?: boolean }) => pide<{ ok: true; aviso?: string }>(`/v1/brands/${id}/videos/propio`, 'POST', cuerpo),
  /** Las palabras de un reel hecho, para subtítulos exactos (gratis; null = no se pudo). */
  palabrasDelReel: (id: string, cuerpo: { month: string; index: number }) => pide<{ palabras: Palabra[] | null }>(`/v1/brands/${id}/videos/palabras`, 'POST', cuerpo),
  /** Elegir el nombre de una marca nueva (ideas de IGOR o uno propio). */
  nombre: (id: string, nombre: string) => pide<{ ok: true }>(`/v1/brands/${id}/nombre`, 'PATCH', { nombre }),
  /** «Aún no tengo redes»: el paso queda listo sin revisar nada. */
  sinRedes: (id: string) => pide<{ ok: true }>(`/v1/brands/${id}/sin-redes`, 'POST', {}),
  identidad: (id: string, cambios: Record<string, unknown>) => pide<{ ok: true }>(`/v1/brands/${id}/identidad`, 'PATCH', cambios),
  /** Seguir sin capturas de la app/web en un video del estudio. */
  sinCapturas: (id: string, prod: string) => pide<{ ok: true }>(`/v1/brands/${id}/estudio/${prod}/capturas`, 'PATCH', {}),
  /** Añade o quita a una persona de las que salen en los reels (hasta tres). */
  presentador: (id: string, cambios: { n: number; quitar?: boolean; borrar?: boolean }) =>
    pide<{ ok: true; elegidos: Array<{ n: number; name: string }> }>(`/v1/brands/${id}/presentador`, 'PATCH', cambios),

  redes: (id: string) => pide<{ cuentas: Cuenta[]; elegidas: string[]; puedeElegir: boolean }>(`/v1/brands/${id}/redes`),
  guardarRedes: (id: string, accountIds: string[]) => pide<{ ok: true; elegidas: string[] }>(`/v1/brands/${id}/redes`, 'PUT', { accountIds }),
  conectarRed: (id: string, red: string) => pide<{ url: string }>(`/v1/brands/${id}/redes/conectar`, 'POST', { red }),
  /** Se llama al volver de la ventana del permiso. Puede pedir que se elija la página (code "elige_pagina"). */
  confirmarRed: (id: string, cuerpo: { oauthId?: string; elegida?: Record<string, unknown> } = {}) =>
    pide<{ ok: boolean; code?: string; cuentas?: Cuenta[]; elegidas?: string[]; opciones?: Array<Record<string, unknown>>; reconectada?: boolean }>(
      `/v1/brands/${id}/redes/confirmar`, 'POST', cuerpo,
    ),
  quitarRed: (id: string, cuentaId: string) => pide<{ ok: true }>(`/v1/brands/${id}/redes/${cuentaId}`, 'DELETE'),

  programacion: (id: string, month?: string | null) =>
    pide<{ publicaciones: Array<Record<string, unknown>>; aMano?: Array<{ index: number; date: string; time: string; format: string; title: string; texto: string; archivos: string[] }> }>(`/v1/brands/${id}/programacion${month ? `?month=${month}` : ''}`),
  /** Cambia el día, la hora o las redes de una pieza del plan; si estaba programada, se reprograma. */
  moverPieza: (id: string, cambio: { month: string; index: number; date?: string; time?: string; platforms?: string[] }) =>
    pide<{ ok: true; aviso?: string }>(`/v1/brands/${id}/plan/pieza`, 'PATCH', cambio),
  programar: (id: string, cuerpo: { month: string; indices?: number[]; rehacer?: boolean }) =>
    pide<{ ok: true; programadas: Array<{ index: number }>; omitidas: Array<{ index: number; motivo: string }> }>(`/v1/brands/${id}/programacion`, 'POST', cuerpo),
  /** Medición de un mes: el total de las cuentas (HighLevel) y cómo le fue a cada pieza publicada. */
  medicion: (id: string, month: string) => pide<Medicion>(`/v1/brands/${id}/medicion?month=${month}`),
  /** Vuelve a medir las piezas publicadas del mes (en segundo plano). */
  medir: (id: string, month: string) => pide<{ ok: true; enCurso?: boolean }>(`/v1/brands/${id}/medicion`, 'POST', { month }),
  /** La zona horaria de la marca (null = la de su país). */
  cambiarZona: (id: string, timezone: string | null) => pide<{ ok: true; timezone: string }>(`/v1/brands/${id}/zona`, 'PATCH', { timezone }),
  cancelar: (id: string, pubId: string) => pide<{ ok: true }>(`/v1/brands/${id}/programacion/${pubId}`, 'DELETE'),
  /** Completa los «[dato a rellenar]» de una pieza (sin créditos). `enElDiseno`: hay que rehacer su diseño. */
  completarDatos: (id: string, cuerpo: { month: string; index: number; valores: Record<string, string> }) =>
    pide<{ ok: true; enElDiseno: boolean; enElVideo: boolean; faltan: string[] }>(`/v1/brands/${id}/textos/datos`, 'PATCH', cuerpo),
};
